/**
 * StudentPrerequisiteQuiz — le quiz de prérequis à la place de l'énoncé
 * (`<StudentPrerequisiteQuizPage>`), tant qu'il n'est pas passé. Comme l'ancien
 * (`PrerequisiteQuizGate`, puis le quiz en checkpoint plein écran), il couvre toute l'activité, au
 * téléphone comme en grand écran, en live comme hors live : une question à la fois, sans fil.
 *
 * La porte est au moteur (`usePrerequisiteGate`, arrivé par `useStudentActivityView`) ; la
 * conversation du quiz aussi (`useCheckpointConversation`, une neuve à chaque ouverture : elle garde
 * les réponses, soumet la tentative, donne le pétale et l'avis). Ici, ce que seul l'écran sait faire :
 * la réponse choisie avant « Question suivante », les sons, le signalement d'une question, et la
 * sortie (le retour de l'intro ramène en arrière, et seulement là où il y a un retour).
 */
import React, { useState } from "react"

import { Snackbar } from "../../../../../../design-system"
import { StudentPrerequisiteQuizPage } from "../../../../../../design-system/pages/StudentPrerequisiteQuizPage"
import { QuizQuestionSignalSheet } from "../../../../../../design-system/organisms/QuizQuestionSignalSheet"
import { type PrerequisiteGate } from "../../../../../../engine/activity/prerequisite/usePrerequisiteGate"
import { useQuizQuestionSignal } from "../../../../../../engine/activity/prerequisite/useQuizQuestionSignal"
import { useCheckpointConversation } from "../../../../../../engine/precall/checkpoint/useCheckpointConversation"
import { prerequisiteQuizStep } from "../../../../view-models/student/Activity/prerequisiteQuizStepViewModel"
import { playStudentSound, SAM_SOUNDS } from "../studentSounds"

type Props = {
    gate: PrerequisiteGate
    /** Le titre de l'activité. */
    title: string
    /** `inline` dans le live. */
    heading?: "bar" | "inline"
    /** Le retour de l'activité. Absent (en live) : la porte n'a pas de sortie. */
    onLeave?: () => void
    /** Dans le live : les sons de Sam y prennent la branche du live. */
    inLive?: boolean
}

function QuizRun({ gate, title, heading, inLive = false }: Props) {
    const checkpoint = useCheckpointConversation({
        conversation: gate.state.conversation ?? {},
        role: "student",
        surface: "checkpoint_overlay",
        activityId: gate.state.activityId,
        api: gate.api ?? undefined,
        // En live, la branche du live : celle d'avant l'appel réglerait la session audio sous l'appel.
        onSound: (sound) => playStudentSound(SAM_SOUNDS[sound], inLive, sound),
        onClosed: gate.closed,
    })
    const [selected, setSelected] = useState<string | null>(null)
    const step = prerequisiteQuizStep({ gate: gate.state, checkpoint: checkpoint.state, selectedKey: selected })
    const questionId = step.kind === "question" ? (gate.state.quiz?.questions?.[step.index]?.id ?? null) : null
    const signal = useQuizQuestionSignal({ activityId: gate.state.activityId, questionId })

    return (
        <>
            <StudentPrerequisiteQuizPage
                title={title}
                heading={heading}
                step={step}
                onStart={gate.start}
                onSelect={setSelected}
                onNext={() => {
                    if (step.kind !== "question" || step.selectedKey == null) return
                    setSelected(null)
                    checkpoint.reply(step.selectedKey)
                }}
                onClose={checkpoint.canClose ? checkpoint.close : undefined}
                onFeedback={(value) => checkpoint.giveFeedback(value === "positive" ? 1 : -1)}
                onSeeCorrection={checkpoint.close}
                onSignal={questionId != null ? signal.open : undefined}
            />
            <QuizQuestionSignalSheet
                visible={signal.state.open}
                type={signal.state.type}
                comment={signal.state.comment}
                sending={signal.state.sending}
                failed={signal.state.failed}
                onType={signal.setType}
                onComment={signal.setComment}
                onSend={signal.send}
                onClose={signal.close}
            />
            <Snackbar
                visible={signal.state.notice != null}
                message={signal.state.notice ?? ""}
                tone="success"
                onDismiss={signal.dismissNotice}
            />
        </>
    )
}

export function StudentPrerequisiteQuiz(props: Props) {
    const { gate, title, heading, onLeave } = props
    const { state } = gate

    if (state.status === "passed") return null
    if (state.status === "quiz" && gate.api != null) return <QuizRun key={state.session} {...props} />

    return (
        <StudentPrerequisiteQuizPage
            title={title}
            heading={heading}
            onBack={onLeave}
            step={prerequisiteQuizStep({ gate: state, checkpoint: null, selectedKey: null })}
            onStart={gate.start}
            onSelect={NOOP}
            onNext={NOOP}
            onFeedback={NOOP}
            onSeeCorrection={NOOP}
        />
    )
}

const NOOP = () => {}
